import React, { useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import moment from "moment";
import { Waiting, Pagination } from "components/ui";
import EmailRecordProfile from "components/contact-records/email-profile";

// Columns that support server-side sort (must match the backend switch: sent | name | company | category).
const SORTABLE = {
  sent: "Sent",
  name: "Patient",
  company: "Company",
  category: "Category"
};

const STATUS_CLASS = {
  Opened: "status-opened",
  Delivered: "status-delivered",
  Bounced: "status-bounced",
  Deferred: "status-deferred",
  None: "status-none"
};

const SortHeader = ({ column, label, sortBy, sortDir, onSort }) => {
  const active = sortBy === column;
  const arrow = active ? (sortDir === "asc" ? " ▲" : " ▼") : "";
  return (
    <th className="sortable-header" onClick={() => onSort(column)}>
      {label}
      {arrow}
    </th>
  );
};

SortHeader.propTypes = {
  column: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  sortBy: PropTypes.string,
  sortDir: PropTypes.string,
  onSort: PropTypes.func.isRequired
};

const PatientActivityTable = ({
  result,
  isFetching,
  page,
  pageSize,
  sortBy,
  sortDir,
  search,
  onPageChange,
  onSort,
  onSearch
}) => {
  const [searchText, setSearchText] = useState(search || "");
  const [expanded, setExpanded] = useState(null); // ContactGuid of the open timeline row

  const rows = (result && result.Rows) || [];
  const totalCount = (result && result.TotalCount) || 0;

  const submitSearch = e => {
    e.preventDefault();
    onSearch(searchText.trim());
  };

  const toggleRow = contactGuid =>
    setExpanded(prev => (prev === contactGuid ? null : contactGuid));

  return (
    <div className="patient-activity">
      <div className="patient-activity-header">
        <h3>Patient Activity</h3>
        <form className="patient-search" onSubmit={submitSearch}>
          <input
            type="text"
            placeholder="Search name or account #"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
          />
          <button type="submit" className="button tiny secondary">
            Search
          </button>
        </form>
      </div>

      {isFetching ? (
        <Waiting />
      ) : (
        <React.Fragment>
          <table className="data-table patient-activity-table">
            <thead>
              <tr>
                <th className="expander-col" />
                <SortHeader column="sent" label={SORTABLE.sent} sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
                <SortHeader column="name" label={SORTABLE.name} sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
                <th>Account #</th>
                <SortHeader column="company" label={SORTABLE.company} sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
                <th>Team</th>
                <th>Coach</th>
                <SortHeader column="category" label={SORTABLE.category} sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
                <th>Status</th>
                <th>Opened</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={10} className="empty-row">
                    No email activity matches these filters.
                  </td>
                </tr>
              ) : (
                rows.map(row => (
                  <React.Fragment key={row.ContactGuid}>
                    <tr>
                      <td className="expander-col">
                        <button
                          className="expander-toggle"
                          onClick={() => toggleRow(row.ContactGuid)}
                          aria-label="Toggle email timeline"
                        >
                          {expanded === row.ContactGuid ? "−" : "+"}
                        </button>
                      </td>
                      <td>{row.SentDate ? moment(row.SentDate).format("MMM DD, YYYY hh:mm A") : ""}</td>
                      <td>
                        {row.PatientGuid ? (
                          <Link to={`/patients/${row.PatientGuid}`}>{row.Name}</Link>
                        ) : (
                          row.Name
                        )}
                      </td>
                      <td>{row.AccountNumber}</td>
                      <td>{row.Company}</td>
                      <td>{row.Team}</td>
                      <td>{row.Coach}</td>
                      <td>{row.Category}</td>
                      <td>
                        <span className={`status-badge ${STATUS_CLASS[row.Status] || "status-none"}`}>
                          {row.Status}
                        </span>
                      </td>
                      <td>{row.Opened ? "Yes" : row.Ignored ? "Ignored" : "No"}</td>
                    </tr>
                    {expanded === row.ContactGuid && (
                      <tr className="timeline-row">
                        <td colSpan={10}>
                          <EmailRecordProfile contactGuid={row.ContactGuid} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>

          {totalCount > pageSize && (
            <Pagination
              page={page}
              per={pageSize}
              count={totalCount}
              action={({ page: newPage }) => onPageChange(newPage)}
            />
          )}
        </React.Fragment>
      )}
    </div>
  );
};

PatientActivityTable.propTypes = {
  result: PropTypes.shape({
    Rows: PropTypes.array,
    TotalCount: PropTypes.number
  }),
  isFetching: PropTypes.bool,
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  sortBy: PropTypes.string,
  sortDir: PropTypes.string,
  search: PropTypes.string,
  onPageChange: PropTypes.func.isRequired,
  onSort: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired
};

export default PatientActivityTable;
